<template>
    <div class="congress-news-editor">
        <!-- Empty State -->
        <div v-if="!modelValue || modelValue.length === 0" class="empty-state congress-schedule-empty-state">
            <p class="mb-4">
                {{ lang('congresses::congresses.news.empty') }}
            </p>
            <Button type="button" size="sm" color="brand" variant="inline" @click="addNews">
                <span class="action-icon">
                    <Icon icon="phosphor/regular/newspaper"></Icon>
                </span>
                <span class="action-label-text">
                    {{ lang('congresses::congresses.news.add_first') }}
                </span>
            </Button>
        </div>

        <!-- News List -->
        <div v-else class="news-list">
            <Sortable :list="modelValue" item-key="_id" tag="div" @end="onReordered">
                <template #item="{ element: newsItem, index }">
                    <div :key="`${newsItem._id}-${index}`" class="tree-line-item grid grid-cols-12">
                        <div class="col-span-1">
                            <TreeLine />
                        </div>
                        <div class="col-span-11">
                            <NewsCard
                                :key="newsItem._id"
                                v-model="modelValue[index]"
                                :index="index"
                                @remove="removeNews(index)"
                            />
                        </div>
                    </div>
                </template>
            </Sortable>

            <!-- Add Button -->
            <div class="congress-schedule-after-event-list-actions">
                <Button type="button" size="sm" color="brand" variant="inline" @click="addNews">
                    <span class="action-icon">
                        <Icon icon="phosphor/regular/newspaper"></Icon>
                    </span>
                    <span class="action-label-text">
                        {{ lang('congresses::congresses.news.add') }}
                    </span>
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid';
import { lang } from '@/lang/composable';
import Sortable from '@src/Common/Components/Sortable.vue';
import Button from '@src/Common/Components/Button.vue';
import TreeLine from '@src/Common/Components/TreeLine.vue';
import NewsCard from './NewsCard.vue';
import type { NewsItem } from '../types';

const modelValue = defineModel<NewsItem[]>();

const ensureIds = (items: NewsItem[]) => {
    return items.map((item) => ({
        ...item,
        _id: item._id || uuidv4(),
    }));
};

if (!modelValue.value) {
    modelValue.value = [];
} else {
    modelValue.value = ensureIds(modelValue.value);
}

const addNews = () => {
    modelValue.value = [
        ...modelValue.value,
        {
            _id: uuidv4(),
            title: '',
            content: '',
            is_published: false,
            published_at: null,
            sort_order: modelValue.value.length,
            metadata: {},
        },
    ];
};

const removeNews = (index: number) => {
    if (confirm(lang('congresses::congresses.news.confirm_remove'))) {
        modelValue.value = modelValue.value.filter((_, idx) => idx !== index);
    }
};

const onReordered = (event: { oldIndex: number; newIndex: number }) => {
    const items = [...modelValue.value];
    const [movedItem] = items.splice(event.oldIndex, 1);
    items.splice(event.newIndex, 0, movedItem);

    items.forEach((item, idx) => {
        item.sort_order = idx;
    });

    modelValue.value = items;
};
</script>
